import type { AxiosResponse } from 'axios';
import type { ApiClientError } from 'api/client';

/**
 * Readers for the HTTP response behind an `ApiClientError`.
 *
 * The client puts it in one of two places depending on which rejection path
 * fired, and callers must not care which:
 *
 * - `axiosError.response` — a transport-level HTTP error (4xx/5xx).
 * - `response` — a 2xx whose body failed the `{ status: false }` envelope
 *   check, which is a normal way for this API to report a failure. No
 *   AxiosError exists on that path, so `axiosError` is undefined.
 *
 * Reading only `axiosError.response` (the shape most of this module was
 * written against) silently misses the whole second class, and used to miss
 * the first as well.
 */
const isAxiosResponseLike = (value: unknown): value is AxiosResponse =>
  value !== null &&
  typeof value === 'object' &&
  'status' in value &&
  typeof (value as { status: unknown }).status === 'number';

export const getErrorResponse = (error: unknown): AxiosResponse | undefined => {
  const apiError = error as ApiClientError | undefined;
  const fromAxios = apiError?.axiosError?.response;
  if (fromAxios) return fromAxios;

  return isAxiosResponseLike(apiError?.response)
    ? apiError?.response
    : undefined;
};

export const getErrorStatus = (error: unknown): number | undefined =>
  getErrorResponse(error)?.status;

const BILLING_TRANSIENT_RETRIES = 2;

/**
 * Retry policy shared by the two app-wide billing reads (`subscription`,
 * `entitlements`).
 *
 * A transient failure (5xx, or network: no HTTP status at all) is retried
 * twice. A 4xx is an answer, not a blip — a 403 on an api that still
 * role-gates the read, a 404 when the NewBilling routes are off for this
 * account — and is never retried.
 *
 * This pairs with `retryOnMount: false` on both queries: because neither query
 * ever holds data on the error path, react-query would otherwise re-fire it
 * for every newly mounted observer (`shouldLoadOnMount`), and these two hooks
 * are mounted by ~40 components across the app.
 */
export const shouldRetryTransientOnly = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = getErrorStatus(error);
  if (status !== undefined && status < 500) return false;
  return failureCount < BILLING_TRANSIENT_RETRIES;
};

/**
 * Machine-readable `reason` out of an error body, when the endpoint sends one.
 *
 * Some billing refusals are routes, not failures — `POST /subscription/reactivate`
 * answers `{ message, reason: 'requires_checkout' }` when the period it would
 * hand back was fully refunded, and the caller must open checkout rather than
 * toast "something went wrong". Branching on `reason` instead of on the message
 * keeps that decision independent of copy changes.
 */
export const getErrorReason = (error: unknown): string | undefined => {
  const data = getErrorResponse(error)?.data as
    | { reason?: unknown }
    | undefined;

  return typeof data?.reason === 'string' ? data.reason : undefined;
};

/**
 * The body's top-level `message`, when it really is a string.
 *
 * Guarded on purpose: an unvalidated read of `response.data.message` will happily
 * hand a caller an object or an array, and storing that in React state throws
 * when it is rendered. This exists so a caller can prefer the api's own sentence
 * over a generic fallback without taking that risk.
 */
export const getResponseMessage = (error: unknown): string | undefined => {
  const data = getErrorResponse(error)?.data as
    | { message?: unknown }
    | undefined;

  // The envelope path can also surface a body that is not an object at all.
  if (typeof data?.message !== 'string' || !data.message) return undefined;

  return data.message;
};

/**
 * The 3D-Secure challenge behind a `requires_action` refusal, or undefined when
 * the error is something else.
 *
 * A bank asking the customer to confirm a payment is not a failed payment, but
 * the API used to report both as `422 { errors: { payment: [...] } }` — so a
 * routine confirmation surfaced as "we couldn't charge your card" with no way
 * forward. The billing endpoints now answer
 * `{ reason: 'requires_action', clientSecret, paymentIntentId, invoiceId }`.
 *
 * Branching on `reason` rather than sniffing the message keeps this independent
 * of copy, exactly as {@link getErrorReason} does for `requires_checkout`.
 */
export interface PaymentChallenge {
  clientSecret: string;
  paymentIntentId?: string;
  invoiceId?: number;
  /**
   * What the api already knows about the intent. When present the browser
   * skips re-reading it: `requires_payment_method` confirms with
   * `paymentMethodId`, `requires_action` runs the next action.
   */
  paymentIntentStatus?: string;
  paymentMethodId?: string | null;
}

export const getPaymentChallenge = (
  error: unknown,
): PaymentChallenge | undefined => {
  if (getErrorReason(error) !== 'requires_action') return undefined;

  const data = getErrorResponse(error)?.data as
    | { clientSecret?: unknown; paymentIntentId?: unknown; invoiceId?: unknown }
    | undefined;

  // Without a client secret there is nothing the browser can do, so this is not
  // a challenge we can present — let the caller fall through to its normal error
  // path rather than opening an empty confirmation.
  if (typeof data?.clientSecret !== 'string' || !data.clientSecret) {
    return undefined;
  }

  return {
    clientSecret: data.clientSecret,
    paymentIntentId:
      typeof data.paymentIntentId === 'string'
        ? data.paymentIntentId
        : undefined,
    invoiceId: typeof data.invoiceId === 'number' ? data.invoiceId : undefined,
  };
};

/**
 * First message for `field` out of a Laravel validation body
 * (`{ errors: { <field>: string[] | string } }`), or undefined when the
 * response carries no error for that field.
 */
export const getFieldError = (
  error: unknown,
  field: string,
): string | undefined => {
  const data = getErrorResponse(error)?.data as
    | { errors?: Record<string, string[] | string | undefined> }
    | undefined;
  const value = data?.errors?.[field];
  const message = Array.isArray(value) ? value[0] : value;

  return typeof message === 'string' && message ? message : undefined;
};
